import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import AppLayout from '../layouts/AppLayout';
import AuthLayout from '../layouts/AuthLayout';
import RoleRoute from '../components/RoleRoute';
import { ROLES } from '../utils/roles';

import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';
import UserForgotPassword from '../pages/auth/UserForgotPassword';
import SuperAdminLogin from '../pages/auth/SuperAdminLogin';
import PortalLogin from '../pages/auth/PortalLogin';
import ClinicSignup from '../pages/auth/ClinicSignup';
import Logout from '../pages/auth/Logout';

import DashboardRouter from '../pages/dashboards/DashboardRouter';
import Analytics from '../pages/dashboards/Analytics';

import Packages from '../pages/packages/Packages';
import PackageDetails from '../pages/packages/PackageDetails';
import PackageForm from '../pages/packages/PackageForm';

import Orders from '../pages/orders/Orders';
import OrderDetails from '../pages/orders/OrderDetails';
import Checkout from '../pages/orders/Checkout';
import PayNow from '../pages/orders/PayNow';
import PaymentReturn from '../pages/orders/PaymentReturn';

import Reports from '../pages/reports/Reports';
import ReportUpload from '../pages/reports/ReportUpload';
import ReportDetails from '../pages/reports/ReportDetails';

import Payments from '../pages/payments/Payments';
import AdminPayouts from '../pages/payouts/AdminPayouts';
import ClinicPayoutSettings from '../pages/payouts/ClinicPayoutSettings';
import PayoutDetails from '../pages/payouts/PayoutDetails';
import MyPayouts from '../pages/payouts/MyPayouts';
import MyBankDetails from '../pages/payouts/MyBankDetails';
import Profile from '../pages/profile/Profile';
import Settings from '../pages/settings/Settings';

import Users from '../pages/users/Users';
import Vendors from '../pages/vendors/Vendors';
import Admins from '../pages/admins/Admins';
import AuditLogs from '../pages/audit/AuditLogs';

import Quiz from '../pages/quiz/Quiz';
import QuizList from '../pages/quiz/QuizList';
import ClinicProfile from '../pages/clinic/ClinicProfile';
import BookingSettings from '../pages/clinic/BookingSettings';
import ClinicCalendar from '../pages/clinic/ClinicCalendar';
import ClinicApprovals from '../pages/vendors/ClinicApprovals';
import ClinicsList from '../pages/clinics/ClinicsList';
import ClinicDetails from '../pages/clinics/ClinicDetails';
import CheckIn from '../pages/clinic/CheckIn';

import MyProgress from '../pages/progress/MyProgress';
import PatientProgress from '../pages/progress/PatientProgress';
import LeaveReview from '../pages/reviews/LeaveReview';
import Referrals from '../pages/referrals/Referrals';
import ReferralAdmin from '../pages/referrals/ReferralAdmin';
import Waitlist from '../pages/waitlist/Waitlist';
import Assessments from '../pages/assessments/Assessments';

import Terms from '../pages/legal/Terms';
import Privacy from '../pages/legal/Privacy';
import MedicalAuthorization from '../pages/legal/MedicalAuthorization';
import LabRelease from '../pages/legal/LabRelease';

import NotFound from '../pages/NotFound';

const ADMINISH = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.STAFF];
const ADMIN_OR_SUPER = [ROLES.SUPER_ADMIN, ROLES.ADMIN];
const SUPER_ONLY = [ROLES.SUPER_ADMIN];
const STAFF_OR_VENDOR = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.STAFF, ROLES.VENDOR];

function AppRoutes() {
  return (
    <Routes>
      {/* Standalone /logout — clears auth then redirects (no layout chrome,
          no auth guard, no AuthLayout redirect). */}
      <Route path="/logout" element={<Logout />} />

      {/* Public auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/user-forgot-password" element={<UserForgotPassword />} />
        <Route path="/super-admin-login" element={<SuperAdminLogin />} />
        <Route path="/portal-login" element={<PortalLogin />} />
        <Route path="/clinic-signup" element={<ClinicSignup />} />
      </Route>

      {/* Authenticated app */}
      <Route element={<AppLayout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardRouter />} />

        {/* Analytics — Super Admin only */}
        <Route path="/analytics" element={<RoleRoute allow={SUPER_ONLY}><Analytics /></RoleRoute>} />

        {/* Packages */}
        <Route path="/packages" element={<Packages />} />
        <Route path="/packages/new" element={<RoleRoute allow={ADMIN_OR_SUPER}><PackageForm /></RoleRoute>} />
        <Route path="/packages/:id" element={<PackageDetails />} />
        <Route path="/packages/:id/edit" element={<RoleRoute allow={ADMIN_OR_SUPER}><PackageForm /></RoleRoute>} />

        {/* Orders */}
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/checkout/:packageId" element={<RoleRoute allow={[ROLES.USER]}><Checkout /></RoleRoute>} />
        {/* One-click "Pay & Book My Consultation" landing target — auto-
            creates order + starts Curlec payment + redirects to hosted checkout. */}
        <Route path="/pay-now" element={<RoleRoute allow={[ROLES.USER]}><PayNow /></RoleRoute>} />
        {/* Curlec redirects users here after paying (or cancelling). Reads
            razorpay_* query params, looks up the order, shows a friendly
            status page. Never mutates state — trusts the webhook only. */}
        <Route path="/payments/return" element={<RoleRoute allow={[ROLES.USER]}><PaymentReturn /></RoleRoute>} />
        <Route path="/orders/:id" element={<OrderDetails />} />

        {/* Eligibility quiz */}
        <Route path="/quiz" element={<RoleRoute allow={[ROLES.USER]}><Quiz /></RoleRoute>} />
        <Route path="/quiz-submissions" element={<RoleRoute allow={ADMINISH}><QuizList /></RoleRoute>} />

        {/* Clinic onboarding + availability */}
        <Route path="/clinic-profile" element={<RoleRoute allow={[ROLES.VENDOR]}><ClinicProfile /></RoleRoute>} />
        <Route path="/clinic/booking-settings" element={<RoleRoute allow={[ROLES.VENDOR]}><BookingSettings /></RoleRoute>} />
        <Route path="/clinic/calendar" element={<RoleRoute allow={[ROLES.VENDOR]}><ClinicCalendar /></RoleRoute>} />

        {/* Clinic approvals queue — admin/staff */}
        <Route path="/clinic-approvals" element={<RoleRoute allow={ADMINISH}><ClinicApprovals /></RoleRoute>} />

        {/* Customer-facing clinic browser + detail */}
        <Route path="/clinics" element={<RoleRoute allow={[ROLES.USER]}><ClinicsList /></RoleRoute>} />
        <Route path="/clinics/:id" element={<RoleRoute allow={[ROLES.USER]}><ClinicDetails /></RoleRoute>} />

        {/* Clinic counter check-in */}
        <Route path="/check-in" element={<RoleRoute allow={[ROLES.VENDOR]}><CheckIn /></RoleRoute>} />

        {/* Progress tracking */}
        <Route path="/my-progress" element={<RoleRoute allow={[ROLES.USER]}><MyProgress /></RoleRoute>} />
        <Route path="/progress/orders/:orderId" element={<RoleRoute allow={STAFF_OR_VENDOR}><PatientProgress /></RoleRoute>} />

        {/* Reviews */}
        <Route path="/reviews/leave/:bookingId" element={<RoleRoute allow={[ROLES.USER]}><LeaveReview /></RoleRoute>} />

        {/* Referrals */}
        <Route path="/referrals" element={<RoleRoute allow={[ROLES.USER, ROLES.VENDOR]}><Referrals /></RoleRoute>} />
        <Route path="/admin/referrals" element={<RoleRoute allow={ADMINISH}><ReferralAdmin /></RoleRoute>} />
        <Route path="/admin/waitlist"     element={<RoleRoute allow={ADMIN_OR_SUPER}><Waitlist /></RoleRoute>} />
        <Route path="/admin/assessments"  element={<RoleRoute allow={ADMIN_OR_SUPER}><Assessments /></RoleRoute>} />

        {/* Legal pages — accessible from anywhere */}
        <Route path="/legal/terms" element={<Terms />} />
        <Route path="/legal/privacy" element={<Privacy />} />
        <Route path="/legal/medical" element={<MedicalAuthorization />} />
        <Route path="/legal/lab-release" element={<LabRelease />} />

        {/* Reports */}
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/upload/:orderId" element={<RoleRoute allow={[ROLES.VENDOR]}><ReportUpload /></RoleRoute>} />
        <Route path="/reports/:id" element={<ReportDetails />} />

        {/* Payments */}
        <Route path="/payments" element={<Payments />} />

        {/* Payouts — admin */}
        <Route path="/admin/payouts"                       element={<RoleRoute allow={ADMIN_OR_SUPER}><AdminPayouts /></RoleRoute>} />
        <Route path="/admin/payouts/:id"                   element={<RoleRoute allow={ADMIN_OR_SUPER}><PayoutDetails /></RoleRoute>} />
        <Route path="/admin/clinics/:id/payout-settings"   element={<RoleRoute allow={ADMIN_OR_SUPER}><ClinicPayoutSettings /></RoleRoute>} />

        {/* Payouts — clinic */}
        <Route path="/my-payouts"                          element={<RoleRoute allow={[ROLES.VENDOR]}><MyPayouts /></RoleRoute>} />
        <Route path="/my-payouts/:id"                      element={<RoleRoute allow={[ROLES.VENDOR]}><PayoutDetails /></RoleRoute>} />
        <Route path="/clinic/bank-details"                 element={<RoleRoute allow={[ROLES.VENDOR]}><MyBankDetails /></RoleRoute>} />

        {/* People */}
        <Route path="/users" element={<RoleRoute allow={ADMINISH}><Users /></RoleRoute>} />
        <Route path="/vendors" element={<RoleRoute allow={ADMINISH}><Vendors /></RoleRoute>} />
        <Route path="/admins" element={<RoleRoute allow={SUPER_ONLY}><Admins /></RoleRoute>} />

        {/* System */}
        <Route path="/audit-logs" element={<RoleRoute allow={SUPER_ONLY}><AuditLogs /></RoleRoute>} />

        {/* Personal */}
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default AppRoutes;
